import { CommonModule, NgIf } from "@angular/common";
import { Component, Input, OnChanges, OnInit, signal, SimpleChanges } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from "@angular/material/input";
import { TranslatePipe } from "@ngx-translate/core";
import { throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService, HttpErrorResponse } from "../../services/auth.service";
import { Status } from "../../shared/constants/status.enum";
import { MigrationFailComponent } from "../migration-fail/migration-fail.component";
import { MigrationInProgressComponent } from "../migration-in-progress/migration-in-progress.component";
import { MigrationSuccessComponent } from "../migration-success/migration-success.component";
import { isEmail } from "../../utils/email.util";
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from "../../constants";
import { ApiConvertError, ApiErrorResponse } from "../../types/api.error.type";

@Component({
  selector: "app-migration-sign-up",
  imports: [
    CommonModule,
    MatButton,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatSuffix,
    MigrationFailComponent,
    MigrationInProgressComponent,
    MigrationSuccessComponent,
    NgIf,
    ReactiveFormsModule,
    TranslatePipe
  ],
  standalone: true,
  templateUrl: "./migration-sign-up.component.html",
  styleUrl: "./migration-sign-up.component.scss"
})
export class MigrationSignUpComponent implements OnInit, OnChanges {
  @Input() email?: string | null;
  @Input() oldPassword?: string | null;
  @Input() username?: string | null;
  @Input() token?: string | null;

  protected form: FormGroup;
  protected signUpStatus = Status.NEW;
  protected error: ApiConvertError = {}
  protected readonly Status = Status;

  constructor(
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
  ) {
    this.form = this._formBuilder.group(
      {
        email: ["", [Validators.required, Validators.pattern(EMAIL_REGEX)]],
        password: ["", [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH), Validators.pattern(PASSWORD_REGEX)]],
        repassword: ["", [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]]
      },
      { validators: this._rePasswordMatchValidator },
    );
  }

  private _rePasswordMatchValidator(formGroup: FormGroup) {
    const passwordControl = formGroup.get("password");
    const rePasswordControl = formGroup.get("repassword");

    if (!passwordControl || !rePasswordControl) return null;

    const password = passwordControl.value;
    const rePassword = rePasswordControl.value;

    if (rePasswordControl.errors && !rePasswordControl.errors["passwordMismatch"]) {
      return null;
    }

    if (password !== rePassword) {
      rePasswordControl.setErrors({ passwordMismatch: true });
    } else {
      rePasswordControl.setErrors(null);
    }

    return null;
  }

  ngOnInit(): void {
    const email = this.email

    if (email && isEmail(email)) {
      this._updateInputs({ email })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const email = changes['email'].currentValue

    if (email) {
      this._updateInputs({ email })
    }
  }

  showPassword = signal(false);
  showRePassword = signal(false);

  togglePassword(event: MouseEvent) {
    this.showPassword.set(!this.showPassword());

    event.preventDefault();
    event.stopPropagation();
  }

  toggleRePassword(event: MouseEvent) {
    this.showRePassword.set(!this.showRePassword());

    event.preventDefault();
    event.stopPropagation();
  }

  hasMinLengthPwd(): boolean {
    const password = this.form.value.password;
    return password.length >= 10;
  }

  hasLowercasePwd(): boolean {
    const password = this.form.value.password;
    return /[a-z]/.test(password);
  }

  hasUppercasePwd(): boolean {
    const password = this.form.value.password;
    return /[A-Z]/.test(password);
  }

  hasNumberAndSpecial(): boolean {
    const password = this.form.value.password;
    // eslint-disable-next-line no-useless-escape
    return /^(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]).+$/.test(password);
  }

  getPwdStrengthClass(isStrong: boolean): string {
    if (isStrong) {
      return "valid";
    }

    return "";
  }

  getPwdStrengthIcon(isStrong: boolean): string {
    if (isStrong) {
      return "assets/images/svg/ic-check.svg";
    }

    return "assets/images/svg/ic-uncheck.svg";
  }

  handleTryAgain({ status }: { status: Status }) {
    this.signUpStatus = status
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.email = this.form.value.email;
      if (this.email && isEmail(this.email)) {
        this._handleCheckMigrated();
      }
    }
  };

  private _handleCheckMigrated(): void {
    const password = this.form.value.password;
    this.signUpStatus = Status.IN_PROGRESS;

    const params = {
      confirmPassword: this.form.value.repassword,
      email: this.email || "",
      newPassword: password,
      oldPassword: this.token?.length ? undefined : this.oldPassword || "",
      oneTimeTokenAuth: this.token,
      username: this.token?.length ? undefined : this.username || ""
    };

    this._authService
      .migrate(params)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this._extractErrors(error?.error)
          this.signUpStatus = Status.FAIL;

          return throwError(() => error);
        }),
      )
      .subscribe((result) => {
        if (result?.isUserMigrated === false && this.signUpStatus !== Status.SUCCESS) {
          this.signUpStatus = Status.FAIL;
        } else {
          this.signUpStatus = Status.SUCCESS;
        }
      });
  };

  private _updateInputs(inputs: Record<string, string>): void {
    this.form.patchValue(inputs);
  }

  private _extractErrors(apiResponse: ApiErrorResponse): void {
    const context = apiResponse?.context;
    const result: ApiConvertError = {};

    if (context && Array.isArray(context.errors)) {
      context.errors.forEach((err) => {
        if (err.message) {
          result.message = result.message ? [...result.message, err.message] : [err.message];
        }

        if (err.warning) {
          result.warning = result.warning ? [...result.warning, err.warning] : [err.warning];
        }

        if (err.suggestions) {
          result.suggestions = result.suggestions
            ? [...result.suggestions, ...err.suggestions]
            : [...err.suggestions];
        }
      });
    } else {
      result.message = [apiResponse?.message]
    }

    this.error = result;
  }
}
